import { useMemo, useState } from 'react'

import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState
} from '@tanstack/react-table'

import { cn } from '@/utils/cn'

import { ColumnVisibilityToggle } from './components/ColumnVisibilityToggle'
import { DensityToggle } from './components/DensityToggle'
import { TableBody } from './components/TableBody'
import { TableEmpty } from './components/TableEmpty'
import { TableError } from './components/TableError'
import { TableHeader } from './components/TableHeader'
import { TableLoading } from './components/TableLoading'
import { TablePagination } from './components/TablePagination'
import { TableSearch } from './components/TableSearch'
import { TableToolbar } from './components/TableToolbar'
import { useResponsive } from './hooks/useResponsive'
import { useTableState } from './hooks/useTableState'
import { DataTableProvider } from './DataTable.context'
import type { DataTableContextValue, DataTableProps, ExtendedColumnDef } from './DataTable.types'
import { densityClasses } from './DataTable.utils'

export const DataTable = <TData, TValue = unknown>({
  data,
  columns,
  loading = false,
  error = null,
  features = {},
  pagination,
  onSortingChange,
  onSelectionChange,
  onRowClick,
  onSearch,
  onRetry,
  onReset,
  className,
  containerClassName,
  emptyState: EmptyComponent = TableEmpty,
  loadingState: LoadingComponent = TableLoading,
  errorState: ErrorComponent = TableError,
  renderToolbar,
  renderSubRow,
  renderRowActions,
  stickyHeader = false,
  // stickyColumns,
  virtualization = false,
  responsive,
  initialState
}: DataTableProps<TData, TValue>) => {
  // Merge default features
  const enabledFeatures = {
    sorting: true,
    filtering: true,
    pagination: true,
    selection: false,
    search: true,
    export: false,
    columnVisibility: true,
    density: true,
    rowExpansion: false,
    ...features
  }

  // Internal state management
  const { state: tableState, updateState } = useTableState(initialState)

  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting || [])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialState?.columnVisibility || {}
  )
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    initialState?.rowSelection || {}
  )
  const [globalFilter, setGlobalFilter] = useState(initialState?.globalFilter || '')

  // Responsive columns
  const responsiveColumns = columns as ExtendedColumnDef<TData>[]
  const { visibleColumns, responsiveInfo } = useResponsive(responsiveColumns, responsive)

  // Pagination state
  const paginationState: PaginationState = pagination
    ? { pageIndex: pagination.pageIndex, pageSize: pagination.pageSize }
    : { pageIndex: 0, pageSize: 10 }

  // Configure table instance
  const table = useReactTable({
    data,
    columns: visibleColumns as ColumnDef<TData>[],
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      pagination: enabledFeatures.pagination ? paginationState : undefined
    },
    // State change handlers
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater
      setSorting(newSorting)
      onSortingChange?.(newSorting)
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater
      setRowSelection(newSelection)
      onSelectionChange?.(newSelection)
    },
    onGlobalFilterChange: (value) => {
      setGlobalFilter(value)
      onSearch?.(value)
    },
    // Feature models
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enabledFeatures.sorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enabledFeatures.filtering ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enabledFeatures.pagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: enabledFeatures.rowExpansion ? getExpandedRowModel() : undefined,
    getFacetedRowModel: enabledFeatures.filtering ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: enabledFeatures.filtering ? getFacetedUniqueValues() : undefined,
    // Configuration
    enableRowSelection: enabledFeatures.selection,
    enableMultiRowSelection: enabledFeatures.selection,
    enableSubRowSelection: enabledFeatures.selection && enabledFeatures.rowExpansion,
    manualPagination: true,
    pageCount: pagination?.pageCount,
    debugTable: process.env.NODE_ENV === 'development'
  })

  // Context value for child components
  const contextValue: DataTableContextValue<TData> = {
    table,
    features: enabledFeatures,
    state: tableState,
    setState: (value) => {
      if (typeof value === 'function') {
        updateState(value(tableState))
      } else {
        updateState(value)
      }
    },
    responsive
  }

  // Memoize density classes for performance
  const densityClassName = useMemo(() => {
    return densityClasses[tableState.density]
  }, [tableState.density])

  // Handle search changes
  const handleSearch = (value: string) => {
    updateState({ globalFilter: value })
    table.setGlobalFilter(value)
  }

  // Handle density changes
  const handleDensityChange = (density: typeof tableState.density) => {
    updateState({ density })
  }

  // Render conditional states
  if (error) {
    return <ErrorComponent error={error} onRetry={onRetry} />
  }

  // if (loading) {
  //   return <LoadingComponent />
  // }

  // if (!loading && data.length === 0) {
  //   return <EmptyComponent onReset={onReset} />
  // }

  return (
    <DataTableProvider value={contextValue}>
      <div className={cn('space-y-4', containerClassName)}>
        {/* Toolbar */}
        {(renderToolbar ||
          enabledFeatures.search ||
          enabledFeatures.columnVisibility ||
          enabledFeatures.density ||
          enabledFeatures.export) && (
          <TableToolbar>
            {renderToolbar ? (
              renderToolbar(table)
            ) : (
              <>
                {/* Left side - Search */}
                {enabledFeatures.search && (
                  <TableSearch
                    value={globalFilter}
                    onChange={handleSearch}
                    placeholder="Buscar en la tabla..."
                  />
                )}

                {/* Right side - Controls */}
                <div className="flex items-center gap-2">
                  {enabledFeatures.density && (
                    <DensityToggle value={tableState.density} onChange={handleDensityChange} />
                  )}

                  {enabledFeatures.columnVisibility && <ColumnVisibilityToggle table={table} />}

                  {/* Export functionality would go here */}
                  {enabledFeatures.export && (
                    <div>{/* ExportMenu component would be implemented here */}</div>
                  )}
                </div>
              </>
            )}
          </TableToolbar>
        )}

        {/* Selection info */}
        {enabledFeatures.selection && table.getFilteredSelectedRowModel().rows.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
            <span className="text-sm text-blue-700">
              {table.getFilteredSelectedRowModel().rows.length} elemento(s) seleccionado(s)
            </span>
            <button
              onClick={() => setRowSelection({})}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Limpiar selección
            </button>
          </div>
        )}

        {/* Main table */}
        <div
          className={cn(
            'relative overflow-auto border rounded-lg',
            stickyHeader && 'max-h-[600px]',
            className
          )}
        >
          {virtualization ? (
            <TableBody
              table={table}
              onRowClick={onRowClick}
              renderSubRow={renderSubRow}
              renderRowActions={renderRowActions}
              virtualization={virtualization}
              className={densityClassName}
            />
          ) : (
            <table className={cn('w-full caption-bottom text-sm', densityClassName)}>
              <TableHeader
                table={table}
                stickyHeader={stickyHeader}
                enableSorting={enabledFeatures.sorting}
              />
              {!loading && data.length > 0 ? (
                <TableBody
                  table={table}
                  onRowClick={onRowClick}
                  renderSubRow={renderSubRow}
                  renderRowActions={renderRowActions}
                />
              ) : (
                ''
              )}
            </table>
          )}
          {/* Loading and empty states */}
          {loading ? (
            <LoadingComponent />
          ) : !loading && data.length === 0 ? (
            <EmptyComponent onReset={onReset} />
          ) : (
            ''
          )}
        </div>
        {/* Pagination */}
        {enabledFeatures.pagination && pagination && (
          <TablePagination
            table={table}
            pagination={pagination}
            // 2025.07 - Se desconecta el uso de pagination.pageCount, de lo contrario
            //           no muestra las opciones de cantidad de filas por página
            // showRowsPerPage={!pagination.pageCount}
            showRowsPerPage={true}
            showInfo={true}
          />
        )}

        {/* Debug info for development */}
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-4 p-4 bg-gray-50 rounded border text-xs">
            <summary className="cursor-pointer font-medium">Debug Info (Development)</summary>
            <div className="mt-2 space-y-2">
              <div>
                <strong>Table State:</strong>
                <pre className="mt-1 overflow-auto">
                  {JSON.stringify(
                    {
                      sorting,
                      columnFilters,
                      globalFilter,
                      rowSelection,
                      columnVisibility
                    },
                    null,
                    2
                  )}
                </pre>
              </div>
              <div>
                <strong>Responsive Info:</strong>
                <pre className="mt-1 overflow-auto">{JSON.stringify(responsiveInfo, null, 2)}</pre>
              </div>
              <div>
                <strong>Features:</strong>
                <pre className="mt-1 overflow-auto">{JSON.stringify(enabledFeatures, null, 2)}</pre>
              </div>
            </div>
          </details>
        )}
      </div>
    </DataTableProvider>
  )
}

// Export compound components for flexible usage
DataTable.Header = TableHeader
DataTable.Body = TableBody
DataTable.Pagination = TablePagination
DataTable.Toolbar = TableToolbar
DataTable.Search = TableSearch
DataTable.Empty = TableEmpty
DataTable.Loading = TableLoading
DataTable.Error = TableError
